import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  ApiGufEnergycharacteristicsListRequest,
  ApiGufEventsListRequest,
  ApiGufGufsListRequest,
  ApiGufLoopsListRequest,
  ApiGufSurfaceinfiltrationsListRequest,
  ApiGufWellsListRequest,
  DesignInstallationOverview,
  DesignLoop,
  DesignSurfaceInfiltration,
  DesignWell,
  EnergyCharacteristics,
  GUFDetail,
  GUFEvent,
} from "@nens/brostar/openapi-client";
import { DesignInstallation as UploadDesignInstallation } from "@nens/brostar/uploadapi-client";
import { toDateString } from "@nens/brostar/uploadapi-client";

import { getApi } from "@/api/api-configuration";

import { BroPageResponse, fetchAllPages } from "./helpers/fetch-all-pages";
import { queryDirectly } from "./helpers/query-directly";
import { simplifyQueryParam } from "./helpers/simplify-query-key";

const uploadApi = getApi();

// --- SINGLE ---

const getGufSingleQuery = (uuid: string) => ({
  queryKey: ["guf-single", uuid],
  queryFn: () => uploadApi.apiGufGufsRead({ uuid }),
});

export const useGufSingleQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (uuid: string) => queryDirectly<GUFDetail>(queryClient, getGufSingleQuery(uuid));
};

// --- EVENTS SINGLE ---

const getGufEventSingleQuery = (uuid: string) => ({
  queryKey: ["guf-event-single", uuid],
  queryFn: () => uploadApi.apiGufEventsRead({ uuid }),
});

export const useGufEventSingleQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (uuid: string) => queryDirectly<GUFEvent>(queryClient, getGufEventSingleQuery(uuid));
};

// --- EVENTS LIST ---

const getGufEventListQuery = (options: ApiGufEventsListRequest) => ({
  queryKey: ["guf-event-list", simplifyQueryParam(options)],
  queryFn: () => uploadApi.apiGufEventsList(options),
  placeholderData: keepPreviousData,
});

export const useGufEventListQuery = (options: ApiGufEventsListRequest) => {
  return useQuery(getGufEventListQuery(options));
};

// --- LIST ---

const getGufListQuery = (options: ApiGufGufsListRequest) => ({
  queryKey: ["guf-list", simplifyQueryParam(options)],
  queryFn: () => uploadApi.apiGufGufsList(options),
  placeholderData: keepPreviousData,
});

export const useGufListQuery = (options: ApiGufGufsListRequest) => {
  return useQuery(getGufListQuery(options));
};

const getGufWellsAllPagesQuery = ({ ...options }: ApiGufWellsListRequest) => ({
  queryKey: ["guf-well-all", simplifyQueryParam(options)],
  queryFn: () =>
    fetchAllPages((pageOptions) => uploadApi.apiGufWellsList({ ...options, ...pageOptions })),
});

export const useGufWellsAllPagesQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (options: ApiGufWellsListRequest) =>
    queryDirectly<BroPageResponse<DesignWell>>(queryClient, getGufWellsAllPagesQuery(options));
};

const getGufLoopsAllPagesQuery = ({ ...options }: ApiGufLoopsListRequest) => ({
  queryKey: ["guf-loop-all", simplifyQueryParam(options)],
  queryFn: () =>
    fetchAllPages((pageOptions) => uploadApi.apiGufLoopsList({ ...options, ...pageOptions })),
});

export const useGufLoopsAllPagesQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (options: ApiGufLoopsListRequest) =>
    queryDirectly<BroPageResponse<DesignLoop>>(queryClient, getGufLoopsAllPagesQuery(options));
};

const getGufSurfaceinfiltrationsAllPagesQuery = ({
  ...options
}: ApiGufSurfaceinfiltrationsListRequest) => ({
  queryKey: ["guf-surface-infiltration-all", simplifyQueryParam(options)],
  queryFn: () =>
    fetchAllPages((pageOptions) =>
      uploadApi.apiGufSurfaceinfiltrationsList({ ...options, ...pageOptions })
    ),
});

export const useGufSurfaceinfiltrationsAllPagesQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (options: ApiGufSurfaceinfiltrationsListRequest) =>
    queryDirectly<BroPageResponse<DesignSurfaceInfiltration>>(
      queryClient,
      getGufSurfaceinfiltrationsAllPagesQuery(options)
    );
};

const getGufEnergycharacteristicsAllPagesQuery = ({
  ...options
}: ApiGufEnergycharacteristicsListRequest) => ({
  queryKey: ["guf-energy-characteristics-all", simplifyQueryParam(options)],
  queryFn: () =>
    fetchAllPages((pageOptions) =>
      uploadApi.apiGufEnergycharacteristicsList({ ...options, ...pageOptions })
    ),
});

export const useGufEnergycharacteristicsAllPagesQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (options: ApiGufEnergycharacteristicsListRequest) =>
    queryDirectly<BroPageResponse<EnergyCharacteristics>>(
      queryClient,
      getGufEnergycharacteristicsAllPagesQuery(options)
    );
};

type InstallationFetchers = {
  fetchGufWells: ReturnType<typeof useGufWellsAllPagesQueryDirectly>;
  fetchGufLoops: ReturnType<typeof useGufLoopsAllPagesQueryDirectly>;
  fetchGufSurfaceInfiltrations: ReturnType<typeof useGufSurfaceinfiltrationsAllPagesQueryDirectly>;
  fetchGufEnergyCharacteristics: ReturnType<
    typeof useGufEnergycharacteristicsAllPagesQueryDirectly
  >;
};

// --- INVALIDATION ---

export const useInvalidateGufQueries = () => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ["guf-single"],
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getGufListQuery({}).queryKey.slice(0, -1),
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: ["guf-well-all"],
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: ["guf-loop-all"],
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: ["guf-surface-infiltration-all"],
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: ["guf-energy-characteristics-all"],
      refetchType: "all",
    });
  }, [queryClient]);
};

// Query to be able to retrieve all installations from the API in order to convert to form
// Helper functions
const toRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
};

const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((part): part is string => typeof part === "string");

  const objectValue = toRecord(value);
  if (!objectValue) return [];

  return Object.values(objectValue).filter((part): part is string => typeof part === "string");
};

// Retrieve the actual installations into the correct format

export const getInstallations = async (
  installations: DesignInstallationOverview[],
  {
    fetchGufWells,
    fetchGufLoops,
    fetchGufSurfaceInfiltrations,
    fetchGufEnergyCharacteristics,
  }: InstallationFetchers
) => {
  const uploadDesignInstallations = await Promise.all(
    (installations ?? [])
      .filter((installation) => installation !== undefined)
      .map(async (installation) => {
        if (!installation.uuid) {
          throw new Error("Design installation is missing uuid and cannot be fetched");
        }
        const [wellsResult, loopsResult, surfaceInfiltrationsResult, energyCharacteristicsResult] =
          await Promise.all([
            fetchGufWells({ installation: installation.uuid }),
            fetchGufLoops({ installation: installation.uuid }),
            fetchGufSurfaceInfiltrations({ installation: installation.uuid }),
            fetchGufEnergyCharacteristics({ installation: installation.uuid }),
          ]);

        const wells = wellsResult.data?.results ?? [];
        const loops = loopsResult.data?.results ?? [];
        const surfaceInfiltrations = surfaceInfiltrationsResult.data?.results ?? [];
        const energyCharacteristics = energyCharacteristicsResult.data?.results ?? [];

        if (energyCharacteristics.length > 1) {
          throw new Error(
            `Expected one or no energyCharacteristics for installation ${installation.uuid}, received ${energyCharacteristics.length}`
          );
        }

        const energyChar = energyCharacteristics.length > 0 ? energyCharacteristics[0] : undefined;

        const energyCharacteristicsData: UploadDesignInstallation["energyCharacteristics"] =
          energyChar
            ? {
                energyCold: toNumberOrUndefined(energyChar.energyCold),
                energyWarm: toNumberOrUndefined(energyChar.energyWarm),
                maximumInfiltrationTemperatureWarm: toNumberOrUndefined(
                  energyChar.maximumInfiltrationTemperatureWarm
                ),
                averageInfiltrationTemperatureCold: toNumberOrUndefined(
                  energyChar.averageInfiltrationTemperatureCold
                ),
                averageInfiltrationTemperatureWarm: toNumberOrUndefined(
                  energyChar.averageInfiltrationTemperatureWarm
                ),
                powerCold: toNumberOrUndefined(energyChar.powerCold),
                powerWarm: toNumberOrUndefined(energyChar.powerWarm),
                power: toNumberOrUndefined(energyChar.power),
                averageColdWater: toNumberOrUndefined(energyChar.averageColdWater),
                averageWarmWater: toNumberOrUndefined(energyChar.averageWarmWater),
                maximumYearQuantityCold: toNumberOrUndefined(energyChar.maximumYearQuantityCold),
                maximumYearQuantityWarm: toNumberOrUndefined(energyChar.maximumYearQuantityWarm),
              }
            : undefined;

        return {
          designInstallationId: installation.designInstallationId ?? "",
          installationFunction: (installation.installationFunction ??
            "geslotenBodemenergiesysteem") as UploadDesignInstallation["installationFunction"],
          designInstallationPos: installation.designInstallationPos ?? "",
          licensedQuantities: [],
          energyCharacteristics: energyCharacteristicsData,
          designLoops: loops.map((loop) => ({
            designLoopId: loop.designLoopId ?? "",
            designLoopPos: loop.designLoopPos ?? "",
            loopType: loop.loopType ?? "",
            startDate: toDateString(loop.startDate),
            endDate: toDateString(loop.endDate),
            geometryType: loop.geometryType === "LineString" ? "LineString" : ("Point" as const),
          })),
          designSurfaceInfiltrations: surfaceInfiltrations.map((surfaceInfiltration) => ({
            designSurfaceInfiltrationId: surfaceInfiltration.designSurfaceInfiltrationId ?? "",
            designSurfaceInfiltrationPos: surfaceInfiltration.designSurfaceInfiltrationPos ?? "",
          })),
          designWells: wells.map((well) => {
            const rawDesignScreen = toRecord(well.designScreen) ?? {};
            const screenType = (rawDesignScreen?.screen_type as string) ?? "";
            const designScreenTop = toNumberOrUndefined(rawDesignScreen?.design_screen_top ?? "");
            const designScreenBottom = toNumberOrUndefined(
              rawDesignScreen?.design_screen_bottom ?? ""
            );
            return {
              designWellId: well.designWellId ?? "",
              wellFunctions: toStringArray(well.wellFunctions) ?? [],
              height: toNumberOrUndefined(well.height) ?? 0,
              wellPos: well.wellPos ?? "",
              geometryPubliclyAvailable: well.geometryPubliclyAvailable ?? "",
              maximumWellDepth: toNumberOrUndefined(well.maximumWellDepth) ?? "",
              maximumWellDepthPubliclyAvailable: well.maximumWellDepthPubliclyAvailable ?? "",
              maximumWellCapacity: toNumberOrUndefined(well.maximumWellCapacity) ?? "",
              relativeTemperature: well.relativeTemperature ?? "",
              designScreen: {
                screenType,
                designScreenTop,
                designScreenBottom,
              },
              installationFunction:
                installation.installationFunction ?? "geslotenBodemenergiesysteem",
            } as UploadDesignInstallation["designWells"][number];
          }),
        } as UploadDesignInstallation;
      })
  );
  return uploadDesignInstallations ?? [];
};
